import { useState } from 'react';

type ViewState = 'rating' | 'negative' | 'positive' | 'thankyou';

export default function ReviewDemo() {
  const [view, setView] = useState<ViewState>('rating');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');

  const handleRatingSelect = (rating: number) => {
    setSelectedRating(rating);
    if (rating <= 3) {
      setView('negative');
    } else {
      setView('positive');
    }
  };

  const handleSubmitFeedback = (e: React.FormEvent) => {
    e.preventDefault();
    setView('thankyou');
  };

  const handleReset = () => {
    setView('rating');
    setSelectedRating(null);
    setFeedback('');
    setName('');
    setContact('');
  };

  const GOOGLE_REVIEW_URL = 'https://g.page/r/your-google-review-link';

  const steps = [
    {
      icon: 'ri-mail-send-line',
      title: 'Automaattinen kysely',
      description: 'Asiakas saa sähköpostilla henkilökohtaisen palautekyselyn juuri oikeaan aikaan – ilman manuaalista työtä sinulta.'
    },
    {
      icon: 'ri-route-line',
      title: 'Älykäs ohjaus',
      description: 'Tyytyväiset asiakkaat ohjataan suoraan Google-arvosteluun. Tyytymättömät ohjataan sinulle – ei julkisesti nettiin.'
    },
    {
      icon: 'ri-star-line',
      title: 'Arvostelut kasvavat',
      description: 'Saat jatkuvasti uusia aitoja Google-arvosteluja automaattisesti, ilman että sinun tarvitsee pyytää niitä itse.'
    }
  ];

  return (
    <section id="demo" className="py-16 md:py-28 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#2563EB]/8 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#1E3A8A]/8 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 md:mb-16 animate-[fadeInUp_0.8s_ease-out]">
          <div className="inline-block bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white px-5 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
            Interaktiivinen demo
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Kokeile itse – näin se toimii
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Klikkaa tähtiä ja koe asiakkaasi matka omin käsin
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">

          {/* LEFT: Widget demo */}
          <div className="flex flex-col items-center animate-[fadeInUp_0.8s_ease-out]">
            {/* Glow effect behind mockup */}
            <div className="relative w-full max-w-md mx-auto lg:max-w-none">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#2563EB]/20 to-[#1E3A8A]/20 rounded-3xl blur-2xl pointer-events-none"></div>
              {/* Browser chrome mockup */}
              <div className="relative w-full shadow-[0_32px_80px_-12px_rgba(30,58,138,0.35)] rounded-2xl">
                <div className="bg-gray-200 rounded-t-2xl px-3 md:px-4 py-2 md:py-3 flex items-center gap-2">
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-red-400"></div>
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-green-400"></div>
                  <div className="flex-1 bg-white rounded-full px-3 md:px-4 py-1 text-xs text-gray-400 ml-2">
                    boostio.fi/palaute
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-b-2xl p-4 md:p-8 border border-gray-200 border-t-0">
                  {/* Logo */}
                  <div className="flex justify-center mb-4 md:mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] rounded-xl">
                        <i className="ri-bar-chart-grouped-line text-white text-base md:text-lg"></i>
                      </div>
                      <span className="text-xl md:text-2xl font-extrabold text-gray-900 tracking-tight">Boostio</span>
                    </div>
                  </div>

                  {/* Card */}
                  <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border border-gray-100">
                    {view === 'rating' && (
                      <div className="animate-[fadeIn_0.4s_ease-out]">
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
                          Kuinka tyytyväinen olit kokemukseesi?
                        </h3>
                        <div className="flex justify-center gap-2 md:gap-4 mt-6 md:mt-8">
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <button
                              key={rating}
                              onClick={() => handleRatingSelect(rating)}
                              className="w-12 h-12 md:w-16 md:h-16 flex flex-col items-center justify-center bg-gray-50 border-2 border-gray-200 rounded-xl hover:border-[#2563EB] hover:shadow-md transition-all duration-200 cursor-pointer group hover:scale-110 transform"
                            >
                              <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                                <i className="ri-star-fill text-base md:text-lg text-gray-300 group-hover:text-[#2563EB] transition-colors"></i>
                              </div>
                              <span className="text-lg md:text-xl font-bold text-gray-700 group-hover:text-[#2563EB] transition-colors leading-none mt-1">
                                {rating}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {view === 'negative' && (
                      <div className="animate-[slideIn_0.4s_ease-out]">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 text-center mb-2">
                          Haluamme korjata tilanteen.
                        </h3>
                        <p className="text-sm text-gray-500 text-center mb-4 md:mb-5">Kerro lyhyesti, mitä tapahtui.</p>
                        <form onSubmit={handleSubmitFeedback} className="space-y-3">
                          <textarea
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Kirjoita palaute tähän…"
                            maxLength={500}
                            rows={3}
                            className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-xl focus:border-[#2563EB] focus:outline-none text-sm resize-none"
                          />
                          <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nimi (valinnainen)"
                            className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-xl focus:border-[#2563EB] focus:outline-none text-sm"
                          />
                          <input
                            type="text"
                            value={contact}
                            onChange={(e) => setContact(e.target.value)}
                            placeholder="Puhelin tai sähköposti (valinnainen)"
                            className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-200 rounded-xl focus:border-[#2563EB] focus:outline-none text-sm"
                          />
                          <button
                            type="submit"
                            className="w-full bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white py-3 rounded-xl font-semibold text-sm hover:from-[#172554] hover:to-[#1E3A8A] transition-all cursor-pointer whitespace-nowrap shadow-lg"
                          >
                            Lähetä palaute
                          </button>
                        </form>
                      </div>
                    )}

                    {view === 'positive' && (
                      <div className="animate-[slideIn_0.4s_ease-out] text-center">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Mahtavaa kuulla! 🙌</h3>
                        <p className="text-sm text-gray-500 mb-4 md:mb-5">Voisitko vielä jättää arvostelun Googleen?</p>
                        <a
                          href={GOOGLE_REVIEW_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block w-full bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white py-3 md:py-4 rounded-xl font-semibold text-sm hover:from-[#172554] hover:to-[#1E3A8A] transition-all cursor-pointer whitespace-nowrap shadow-lg"
                        >
                          Jätä arvostelu Googleen
                        </a>
                      </div>
                    )}

                    {view === 'thankyou' && (
                      <div className="animate-[fadeIn_0.4s_ease-out] text-center py-4 md:py-6">
                        <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                          <i className="ri-check-line text-3xl md:text-4xl text-green-600"></i>
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">Kiitos palautteesta.</h3>
                        <p className="text-sm text-gray-500">Palaamme tarvittaessa pian.</p>
                      </div>
                    )}
                  </div>

                  {/* Reset button */}
                  {view !== 'rating' && (
                    <div className="text-center mt-4 md:mt-5">
                      <button
                        onClick={handleReset}
                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer underline whitespace-nowrap"
                      >
                        ↺ Kokeile uudelleen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Explanation */}
          <div className="space-y-6 md:space-y-8 animate-[fadeInUp_0.8s_ease-out_0.2s_both] text-center lg:text-left">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">
                Asiakkaasi saa yksinkertaisen kyselyn – sinä saat arvosteluja
              </h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Boostio lähettää asiakkaillesi automaattisesti lyhyen palautekyselyn. Koko prosessi kestää asiakkaalta alle 10 sekuntia – ja sinulle se on täysin automaattinen.
              </p>
            </div>

            <div className="space-y-5 md:space-y-6">
              {steps.map((step, idx) => (
                <div key={idx} className="flex gap-4 md:gap-5 group items-start text-left">
                  <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] rounded-2xl flex items-center justify-center text-white text-xl md:text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <i className={step.icon}></i>
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg font-bold text-gray-900 mb-1 group-hover:text-[#1E3A8A] transition-colors">
                      {step.title}
                    </h4>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="pt-4 flex flex-col items-center lg:items-start">
              <a
                href="#pricing"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
              >
                Aloita ilmainen kokeilu
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </a>
              <p className="text-sm text-gray-400 mt-3 text-center lg:text-left">14 päivää ilmaiseksi, ei sitoutumista</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}